import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, X } from 'lucide-react';

type Tone = 'danger' | 'default';

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Optional third button (e.g. "cancel without refund"). */
  secondaryLabel?: string;
  tone?: Tone;
}

/** Result: 'confirm' (primary), 'secondary' (optional middle button), or 'cancel'. */
export type ConfirmResult = 'confirm' | 'secondary' | 'cancel';

type ConfirmFn = (opts: ConfirmOptions) => Promise<ConfirmResult>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * App-wide styled confirmation dialog, replacing native window.confirm/alert for
 * sensitive actions. Usage:
 *   const confirm = useConfirm();
 *   if ((await confirm({ title, message, tone: 'danger' })) === 'confirm') { ... }
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((r: ConfirmResult) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((o) => {
    setOpts(o);
    return new Promise<ConfirmResult>((resolve) => { resolver.current = resolve; });
  }, []);

  const settle = (r: ConfirmResult) => {
    setOpts(null);
    resolver.current?.(r);
    resolver.current = null;
  };

  const danger = opts?.tone === 'danger';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts && (
        <div
          className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => settle('cancel')}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-slide-up"
            role="alertdialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${danger ? 'bg-rose-100 text-rose-600' : 'bg-teal-100 text-teal-600'}`}>
                  <AlertTriangle size={22} />
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <h3 className="text-lg font-bold text-slate-900">{opts.title}</h3>
                  {opts.message && <div className="mt-1.5 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{opts.message}</div>}
                </div>
                <button onClick={() => settle('cancel')} className="text-slate-300 hover:text-slate-500 shrink-0"><X size={18} /></button>
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2 bg-slate-50 px-6 py-4 border-t border-slate-100">
              <button
                onClick={() => settle('cancel')}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg"
              >
                {opts.cancelLabel ?? 'Cancelar'}
              </button>
              {opts.secondaryLabel && (
                <button
                  onClick={() => settle('secondary')}
                  className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  {opts.secondaryLabel}
                </button>
              )}
              <button
                onClick={() => settle('confirm')}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-lg ${danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-teal-600 hover:bg-teal-700'}`}
              >
                {opts.confirmLabel ?? 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}
